import axios from "../axios/axios";
import { MarketplaceResponse } from "../types/shared";
import { ConsultPharmacySelection } from "../types/consultOrder.types";

// ITAM-2887: pharmacy lookup for the per-draft picker (Q3, Q11). Verified
// against apoteka-back dev @ 803e8558, MarketplaceController: `/marketplace/names`
// has no auth annotation and answers 400 (not 200 []) when the list is empty;
// `/marketplace/{marketplaceId}/{branchMarketplaceId}` is the detail the
// pharmacist build already fetched as enrichment and carries `haveRFZO`.

/** Row of GET /marketplace/names (note the `idMarketplace` spelling). */
export interface MarketplaceNameRow {
  idMarketplace: number;
  idBranchMarketplace: number;
  name: string;
  branchMarketplaceName: string | null;
  address: string | null;
  location: string | null;
  isClickCollect: number | null;
}

/** GET /marketplace/names; a 400 means "no rows" and resolves to `[]`. */
export const getPharmacyNames = (
  signal?: AbortSignal,
): Promise<MarketplaceNameRow[]> =>
  axios
    .get<MarketplaceNameRow[]>("/marketplace/names", { signal })
    .then((res) => (Array.isArray(res.data) ? res.data : []))
    .catch((error) => {
      if (error?.response?.status === 400) {
        return [];
      }
      throw error;
    });

/** GET /marketplace/{m}/{b}: the branch detail (haveRFZO, coordinates, names). */
export const getPharmacyDetail = (
  marketplaceId: number,
  branchMarketplaceId: number,
  signal?: AbortSignal,
): Promise<MarketplaceResponse> =>
  axios
    .get<MarketplaceResponse>(
      `/marketplace/${marketplaceId}/${branchMarketplaceId}`,
      { signal },
    )
    .then((res) => res.data);

/** Only click-and-collect pharmacies can take a reservation. */
export const isPickableRow = (row: MarketplaceNameRow): boolean =>
  row.isClickCollect === 1;

/**
 * Map a names row to the draft selection. haveRFZO and the coordinates are
 * unknown until the detail lands (the names row carries no coordinates), so
 * the selection starts in the "loading" detail state.
 */
export const selectionFromRow = (
  row: MarketplaceNameRow,
): ConsultPharmacySelection => ({
  marketplaceId: row.idMarketplace,
  branchMarketplaceId: row.idBranchMarketplace,
  name: row.name,
  branchName: row.branchMarketplaceName,
  address: row.address,
  haveRFZO: null,
  latitude: null,
  longitude: null,
  detailStatus: "loading",
});

/** A detail coordinate as the delivery pre-checks need it: 0 or missing is null. */
export const detailCoordinate = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) && value !== 0 ? value : null;

// One list, every pharmacy, fetched once per session and shared by both
// pickers. Small enough to keep in memory; a failed fetch is NOT cached so the
// next picker open retries.
let namesCache: Promise<MarketplaceNameRow[]> | null = null;

export const loadPickablePharmacies = (): Promise<MarketplaceNameRow[]> => {
  if (!namesCache) {
    namesCache = getPharmacyNames()
      .then((rows) => rows.filter(isPickableRow))
      .catch((error) => {
        namesCache = null;
        throw error;
      });
  }
  return namesCache;
};

/** Test seam and logout hygiene. */
export const resetPharmacyNamesCache = (): void => {
  namesCache = null;
};
